/**
 * @format
 * @module lib/commercial/keytar/helpers
 */
import { getProp } from "utils/utils";
import logger from "utils/logger";

const debugTag = "[KEYTAR-HELPERS]";

/**
 * @method isGovtReferrer
 * @description determines whether the referring page was from a Government of Canada property
 * @param {String} [ref = window.document.referrer]
 * @returns {Boolean} true when positive
 */
export function isGovtReferrer(ref = window.document.referrer) {
  return /infomedia\.gc\.ca/im.test(ref) || /tgam\-dev\-dependencies\/amazon\.html/im.test(ref);
}

/**
 * @method isUnionReferrer
 * @description determines whether the referring page was from /union/
 * @param {String} [ref = window.document.referrer]
 * @returns {Boolean} true when positive
 */
export function isUnionReferrer(ref = window.document.referrer) {
  return /\/union\//im.test(ref);
}

export function isFreeToViewReferrer() {
  const union = isUnionReferrer();
  const theFeds = isGovtReferrer();
  return [union, theFeds].some(referrer => referrer === true);
}

/**
 * @method isSpotlight
 * @description determines whether the referring page was from Apple Spotlight(i.e., apple.news)
 * @param {String} [ref = window.document.referrer]
 * @param {String} [search = window.location.search]
 * @returns {Boolean} true when positive
 */
export function isSpotlight(ref = window.document.referrer, search = window.location.search) {
  return /apple\.news/im.test(ref) || /spotlight=true/im.test(search);
}

/**
 * @method isHousePriceDataCentre
 * @description Determine if a url matches the House Price Data Centre page url.
 * The same HPDC page isn't available on all environments, so test for two versions.
 * Local: https://arc-local.theglobeandmail.com/sandbox/real-estate/article-canadas-home-prices-flat-through-the-end-of-summer/
 * Dev:   https://arc-dev.theglobeandmail.com/sandbox/real-estate/article-canadas-home-prices-flat-through-the-end-of-summer/
 * Stage: https://arc-staging.theglobeandmail.com/real-estate/article-canadas-house-price-data-centre/
 * Prod:  https://www.theglobeandmail.com/real-estate/article-canadas-house-price-data-centre/
 * @param {String} [url = window.location] - to test regular express against
 * @returns {Boolean} true when positive
 */
export function isHousePriceDataCentre(url = window.location.toString()) {
  // note the lack of a /g flag here: implementing it may cause /re/.test(str) to do weird things
  // see http://2ality.com/2013/08/regexp-g.html for more info
  const prod = /.*?\/article-canadas-house-price-data-centre\/*/im;
  const sandbox = /.*?\/article-canadas-home-prices-flat-through-the-end-of-summer\/*/im;
  return prod.test(url) || sandbox.test(url);
}

/**
 * @method isCrosswordsPage
 * @description Determine if the page is a puzzle and crosswords page. Excludes puzzle section articles.
 * @param {String} [url = window.location.href.toString()] - to test against
 * @returns {Boolean} true when positive
 */
export function isCrosswordsPage(url = window.location.href.toString()) {
  const section = /\/puzzles-and-crosswords\/(\?|$)/im;
  const puzzles = /\/puzzles-and-crosswords\/(universal-crossword|cryptic-crossword|sudoku-classic)\/(\?|$)/im;
  return section.test(url) || puzzles.test(url);
}

/**
 * @method isFullChartPage
 * @description Determine if the page is a financial full-chart page.
 * @param {String} [url = window.location.toString()] - to test against
 * @returns {Boolean} true when positive
 */
export function isFullChartPage(url = window.location.toString()) {
  return /\/full\-chart\/?/im.test(url);
}

/**
 * @method isNewslettersPage
 * @description Determine if the page is the Newsletters page.
 * @param {String} [url = window.location.href.toString()] - to test against
 * @returns {Boolean} true when positive
 */
export function isNewslettersPage(url = window.location.href.toString()) {
  return /\.theglobeandmail\.com(?::8888)?(?:\/pb)?\/newsletters\/(\?|$)/im.test(url);
}

/**
 * Determine if at least one object method property evaluates to true.
 * Note, that the `allowed` Object param will be mutated.
 * @param {{String:Function}} allowed - kvp or rule names and their test method.
 * @returns {Boolean} true when at least one method returns true
 */
export function processObjectMethods(allowed) {
  return !!Object.keys(allowed).find(key => {
    return (allowed[key] = allowed[key]());
  });
}

/**
 * @method hasParameterMatch
 * @description Determine if story URL contains parameter value that matches whitelisted runtime values.
 * @param {String} [url = window.location.href.toString()] - to test against
 * @param {String} [whitelistedParams = window.tgam.keytar.] - comma delimited list of whitelisted parameter values to match against "parameter" value
 * @param {String} [parameter = "utm_source"] - parameter in current URL to match against whitelist
 * @returns {Boolean} whether or not a match has been found
 */
export function hasParameterMatch(
  url = window.location.href.toString(),
  whitelistedParams = getProp(window, "tgam.keytar.newsletterStoryParamWhitelist", ""),
  parameter = "utm_source"
) {
  // Decodes the "&amp;" when it's encountered, because email.
  // Reference: https://gomakethings.com/decoding-html-entities-with-vanilla-javascript/
  function decodeHTML(html) {
    var txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  const urlDecoded = new URL(decodeHTML(url));
  logger.info(`${debugTag} url decoded: `, urlDecoded);
  const urlDecodedParam = urlDecoded.searchParams.get(parameter);
  logger.info(`${debugTag} url parameter value: `, urlDecodedParam);

  const matchFound = whitelistedParams.split(",").some(whitelistedParam => {
    return whitelistedParam === urlDecodedParam;
  });

  logger.info(`${debugTag} hasParameterMatch()`, {
    urlDecodedParam,
    whitelistedParams,
    matchFound
  });

  return matchFound;
}

/**
 * @method isElectionPage
 * @description Determine if the page is related to the Federal Election 2021
 * @param {Object} config - TGAM config
 * @returns {Boolean} true when positive
 */
export function isElectionPage(config) {
  const electionRelatedPageHidePencil = getProp(
    config,
    "env.electionRelatedPageHidePencil",
    "false"
  );
  const urlRelative = getProp(config, "meta.urlRelative", "");

  logger.info(`${debugTag} isElectionPage:`, {
    electionRelatedPageHidePencil,
    urlRelative
  });

  if (electionRelatedPageHidePencil !== "true") {
    return false;
  }

  // We want to hide the pencil on these pages
  const pages = [
    "/politics/election2019/results",
    "/politics/election2021/results",
    "/politics/federal-election",
    "/politics/federal-election/2019-results",
    "/politics/federal-election/2021-results"
  ];

  const pageMatch = pages.some(page => urlRelative.startsWith(page));
  if (pageMatch) {
    logger.info(`${debugTag} isElectionPage page match: ${pages}`);
    return true;
  }

  return false;
}
